/**
 * PostOpsFilesCard.tsx
 *
 * Post-op photos and documents, replacing the legacy Google Drive panel.
 *
 * Legacy's post-op detail page booted TWO file surfaces on load
 * (lib/patient-details/postop-details.php:1131): loadConsultImages, a real
 * Google Drive/OAuth browser, and loadBAFiles, which already read the docstore
 * root V5 serves. Both are replaced here by the internal Files system, so
 * nothing carries an OAuth token into the page.
 *
 * A thin card over the shared patient-files service rather than a second file
 * browser — uploads, downloads and the presigned path above the size threshold
 * all behave exactly as they do on the patient's Files tab.
 */
import { useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Upload, Trash2, Download } from 'lucide-react';
import { Button, Spinner, ConfirmDialog } from '@/components/ui';
import { toast } from '@/store/toastStore';
import {
  describeUploadFailures,
  uploadEach,
  UPLOAD_FAILED_FALLBACK,
} from '@/pages/patients/files/uploadFailures';
import {
  listFiles,
  uploadFile,
  downloadFile,
  deleteFile,
  type PatientFile,
} from '@/services/patient-files.service';
import { ACCEPT } from '@/services/patientFileTypes';

/**
 * The docstore folder this card is scoped to. Everything the card reads or
 * writes passes this, so it can never browse the whole patient record.
 */
const POST_OPS_FOLDER = 'before_and_after';

export function PostOpsFilesCard({ patientId }: { patientId: number }) {
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<PatientFile | null>(null);

  const queryKey = ['post-ops-files', patientId, POST_OPS_FOLDER];

  const { data: files = [], isLoading, isError } = useQuery({
    queryKey,
    queryFn: () => listFiles(patientId, POST_OPS_FOLDER),
  });

  function refresh() {
    queryClient.invalidateQueries({ queryKey });
  }

  async function handleUpload(list: FileList | null) {
    if (!list || list.length === 0) return;

    const files = Array.from(list);
    setBusy(true);
    try {
      const failures = await uploadEach(
        files,
        (file) => uploadFile(patientId, file, POST_OPS_FOLDER),
        UPLOAD_FAILED_FALLBACK,
      );
      if (failures.length > 0) {
        toast.error('Could not upload', describeUploadFailures(failures, files.length));
      }
    } finally {
      // Refreshed on failure too: the files saved before or after a refused
      // one must show, or they get uploaded twice.
      refresh();
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  async function handleDownload(file: PatientFile) {
    try {
      await downloadFile(patientId, file.name, POST_OPS_FOLDER, file.source);
    } catch {
      toast.error('Could not download', 'That file could not be opened. Try again.');
    }
  }

  async function handleDelete(file: PatientFile) {
    try {
      await deleteFile(patientId, file.name, POST_OPS_FOLDER, file.source);
      refresh();
    } catch {
      toast.error('Could not delete', 'That file was not deleted. Try again.');
    } finally {
      setPendingDelete(null);
    }
  }

  const visible = files.filter((f) => f.type !== 'folder');

  return (
    <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 bg-slate-50 px-4 py-3">
        <h3 className="text-sm font-semibold text-slate-900">Photos &amp; Documents</h3>
        <Button
          size="sm"
          variant="secondary"
          loading={busy}
          onClick={() => inputRef.current?.click()}
          leftIcon={<Upload className="h-4 w-4" />}
        >
          Upload
        </Button>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={ACCEPT}
          className="hidden"
          onChange={(e) => handleUpload(e.target.files)}
        />
      </div>

      <div className="p-4">
        {isLoading ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : isError ? (
          <p className="text-sm text-red-600">
            These files could not be loaded. Refresh the page, or contact support if it keeps happening.
          </p>
        ) : visible.length === 0 ? (
          <p className="text-sm text-slate-500">No photos or documents yet.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {visible.map((file) => (
              <li key={file.name} className="flex items-center justify-between gap-2 py-2">
                <span className="truncate text-sm text-slate-900">{file.name}</span>
                <span className="flex shrink-0 items-center gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDownload(file)}
                    aria-label={`Download ${file.name}`}
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setPendingDelete(file)}
                    aria-label={`Delete ${file.name}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <ConfirmDialog
        isOpen={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        onConfirm={() => {
          if (pendingDelete) void handleDelete(pendingDelete);
        }}
        title="Delete this file?"
        message={`"${pendingDelete?.name ?? ''}" will be removed from this patient's record. This cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
      />
    </section>
  );
}
